import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityBodyAssessWeighIn } from '@reg/shared/model';
import { HttpServiceHelper, IdentityBodyAssessService } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save bodyAssessWeighIn data.
 */
@Injectable()
export class IdentityBodyAssessWeighInService {
    public static get INCLUDE_BODY_ASSESSMENT_W_PERSON(): string {
        return 'bodyAssessment.person';
    }

    private identityApiBaseUrl: string;
    private cachedMap: { [apiRequest: string]: IdentityBodyAssessWeighIn[]; } = {};

    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private identityBodyAssessService: IdentityBodyAssessService,
    ) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all weighIns for a given body assessment
     */
    public getIndex(
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
        displaySpinner: boolean = true,
    ): Observable<IdentityBodyAssessWeighIn[]> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(null, null, includes, queryParams);

        if (!forceRefresh && this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedMap[apiRequestUrl].map((a: IdentityBodyAssessWeighIn) => a.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl, displaySpinner)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyAssessWeighIns: IdentityBodyAssessWeighIn[] = [];
                const included: any[] = response.included;

                const bodyData: any = response.data || [];
                for (const bodyAssessWeighIn of bodyData) {
                    bodyAssessWeighIns.push(
                        (new IdentityBodyAssessWeighIn()).deserialize(bodyAssessWeighIn, included),
                    );
                }

                this.cachedMap[apiRequestUrl] = bodyAssessWeighIns;
                return bodyAssessWeighIns.map((i: IdentityBodyAssessWeighIn) => i.clone());
            }));
    }

    /**
     * Get a single bodyAssessmentWeighIn
     */
    public get(
        identityBodyAssessmentId: string,
        identityBodyAssessWeighInId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityBodyAssessWeighIn> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(
            identityBodyAssessmentId,
            identityBodyAssessWeighInId,
            includes,
            queryParams,
        );

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentityBodyAssessWeighIn().deserialize(response))));
    }

    /**
     * Add or update a bodyAssessmentWeighIn
     */
    public save(
        bodyAssessWeighIn: IdentityBodyAssessWeighIn,
        includes: string[] = null,
        queryParams: string[] = null,
        displaySpinner: boolean = true,
    ): Observable<IdentityBodyAssessWeighIn> {
        queryParams = this.appendGetFields(queryParams);
        this.http.toastService.clear();

        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(
            bodyAssessWeighIn.bodyAssessment.id,
            bodyAssessWeighIn.id,
            includes,
            queryParams,
        );

        if (bodyAssessWeighIn.id) {
            // Update existing BodyAssessWeighIn
            response = this.http.patch(apiRequestUrl, bodyAssessWeighIn.serialize(), displaySpinner);
        } else {
            // Add a new BodyAssessWeighIn
            response = this.http.post(apiRequestUrl, bodyAssessWeighIn.serialize(), displaySpinner);
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityBodyAssessWeighIn()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified ineligibility.
     */
    public delete(bodyAssessWeighIn: IdentityBodyAssessWeighIn): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(bodyAssessWeighIn.bodyAssessment.id, bodyAssessWeighIn.id, null);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Sync weigh ins with the given team and event
     */
    public eventSync(eventId: string, identityTeamId: string): Observable<boolean> {
        const apiUrl:string = this.getApiUrl(null, null);
        const apiRequestUrl: string = `${apiUrl}event-sync?eventId=${eventId}&identityTeamId=${identityTeamId}`;

        return this.http.patch(apiRequestUrl, null)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        bodyAssessmentId: string,
        bodyAssessWeighInId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/body-assessments/`;

        if (bodyAssessmentId) {
            url += `${bodyAssessmentId}/weigh-ins/`;
        } else {
            url += 'weigh-ins/';
        }

        if (bodyAssessWeighInId) {
            url += bodyAssessWeighInId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.cachedMap = {};
        this.identityBodyAssessService.clearCache();
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[bodyAssessmentWeighIn]') !== -1).length === 0) {
            queryParams.push(`fields[bodyAssessmentWeighIn]=${IdentityBodyAssessWeighIn.getFields().join(',')}`);
        }

        return queryParams;
    }
}

